/* Décision de réconciliation du panier — Scoop by Coralie, lot 104.

   RÉSULTAT ATTENDU
   ----------------
   Une seule fonction décide de tout ce que le thème ajoute ou retire du
   panier : `decisionCadeaux(panier, cfg, refus)`. Elle reçoit le panier tel
   que `/cart.js` le rend, la configuration publiée par
   `snippets/sbc-cadeaux-config.liquid`, et la liste des refus de gratuité
   déjà constatés. Elle renvoie une liste d'opérations et l'état à publier.

   POURQUOI CETTE FORME
   --------------------
   1. **Pure.** Aucun réseau, aucun DOM, aucune horloge. La règle métier —
      depuis la décision D19, la seule qui reste : la livraison offerte dès
      100 € — devient donc vérifiable hors du navigateur, par
      `node tools/verif-panier.test.mjs`. Avant le lot 104, la même règle vivait
      dans une fonction `async` de 150 lignes qui relisait le panier entre
      chaque mutation : elle n'était testable qu'à la main, dans un vrai panier.
   2. **Une décision, pas une exécution.** Les opérations sont rendues
      groupées : le moteur (`assets/sbc-panier.js`) les applique en un appel
      réseau au lieu d'un appel par ligne. C'est ce qui remplace la boucle
      « muter puis relire » de douze tours de l'ancien moteur.
   3. **Idempotente.** Appliquée à un panier, puis rejouée sur le panier
      obtenu, la seconde décision est vide. C'est la propriété qui interdit
      l'oscillation ajout/retrait — le défaut qui laissait quatre vidéos dans
      un panier (V17).
   4. **Elle ne promet rien.** Le thème ne décide jamais qu'un produit est
      gratuit : il le constate, ou il se tait.

   LOT 147 — PLUS AUCUN CADEAU AUTOMATIQUE
   ---------------------------------------
   La décision D19 retire le carnet fait main offert dès 63 €. Le thème
   n'ajoute donc plus rien au panier et ne pose plus jamais la marque
   `_sbc_cadeau`. Le seul avantage qui reste est la livraison offerte dès
   100 € — France, Belgique et Monaco — et elle ne touche à aucune ligne.

   Une seule règle de nettoyage subsiste : une ligne encore marquée
   `_sbc_cadeau: 'auto'` — le panier d'une cliente, commencé avant le lot —
   est retirée, gratuite ou facturée, à quelque montant que ce soit. C'est la
   MARQUE qui décide, jamais le produit : un carnet ACHETÉ ne la porte pas et
   n'est jamais touché. `ops.ajouts` reste dans la réponse, toujours vide :
   ses lecteurs continuent de la lire, et un ajout qui reviendrait s'y verrait
   aussitôt.

   LOT 148 — LA VIDÉO N'EST PLUS L'AFFAIRE DU PANIER
   -------------------------------------------------
   Depuis la décision D19 (12 septembre 2026), révisée par D20 (16 septembre
   2026, lot 151), la vidéo de préparation est une option de chaque scoop :
   PAYANTE à 1, 2 et 3 scoops, OFFERTE à partir de 4 scoops (la variante
   « SANS Vidéo » devient alors indisponible dans Shopify, jamais supprimée).
   Elle se choisit sur la fiche produit, par la valeur « AVEC Vidéo » de
   l'option « VIDÉO TIKTOK » de la variante, et son supplément — quand il y en
   a un — est la différence de prix entre les deux variantes sœurs, lue dans
   Shopify, jamais écrite ici.

   Ce fichier ne touche donc plus JAMAIS une ligne vidéo : ni remplacement de
   variante, ni propriété de ligne, ni dédoublonnage. Ce que la cliente a
   choisi sur la fiche est ce qu'elle retrouve au panier, à la caisse et sur sa
   commande — le panier ne bascule plus rien dans son dos.

   Ce qui disparaît avec cette règle : `jumelleVideo`, la propriété visible
   « Vidéo de préparation », le seuil de scoops publié par la configuration —
   s'il y figure encore, il n'est plus lu —, et l'ancienne règle
   « une seule vidéo par commande » (deux lignes « AVEC Vidéo » facturent donc
   maintenant deux suppléments). ⚠ à valider avec Coralie : plus rien
   n'empêche une commande de payer deux fois la même prestation.
*/
(function (global, usine) {
  var api = usine();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (global) global.SbcPanierDecision = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  /* La seule identité qui reste : la marque d'un cadeau posé par le thème
     AVANT le lot 147. Elle ne sert plus qu'à nettoyer. Les propriétés qui
     l'accompagnaient (`_sbc_cadeau_type`, `_sbc_cadeau_palier`,
     `_sbc_cadeau_couleur`) n'ont plus aucun lecteur : la ligne entière part. */
  var MARQUE = '_sbc_cadeau';
  var AUTO = 'auto';

  /* Le compte des scoops n'existe qu'à un seul endroit
     (`assets/sbc-compte-scoops.js`, lot 102). Il est lu par le navigateur sur
     `window`, par Node par `require` relatif. Absent, le compte reste à 0 : il
     ne sert plus qu'à informer l'affichage, jamais à décider d'un avantage. */
  function compteur() {
    if (typeof window !== 'undefined' && window.SbcCompteScoops) return window.SbcCompteScoops;
    if (typeof require === 'function') {
      try { return require('./sbc-compte-scoops.js'); } catch (e) { return null; }
    }
    return null;
  }

  var nb = function (v) { return Number(v) || 0; };
  var props = function (ligne) { return (ligne && ligne.properties) || {}; };
  var estAuto = function (ligne) { return props(ligne)[MARQUE] === AUTO; };

  function promotionLivraisonRelais(cfg, pays, maintenant) {
    var promo = cfg && cfg.promotionLivraisonRelais;
    if (!promo || promo.active !== true || !Array.isArray(promo.pays)) return false;
    var destination = String(pays || promo.paysActif || '').toUpperCase();
    return promo.pays.indexOf(destination) !== -1 &&
      (maintenant == null || Number(maintenant) < Date.parse(promo.fin));
  }

  function decisionCadeaux(panier, cfg, refus) {
    var items = (panier && Array.isArray(panier.items)) ? panier.items : [];
    var listeRefus = Array.isArray(refus) ? refus : [];

    var SEUIL_LIVRAISON = cfg && cfg.livraisonOfferteEligible === true ? nb(cfg.seuilLivraisonCentimes) : 0;
    var IDS_SCOOPS = (cfg && Array.isArray(cfg.produitsEligibles) ? cfg.produitsEligibles : [])
      .map(Number).filter(Boolean);

    /* `ajouts`, `quantites`, `proprietes` et `remplacements` restent dans la
       réponse — toujours vides depuis les lots 147 et 148 — parce que leurs
       lecteurs (`assets/sbc-panier.js`, la contre-épreuve) les lisent encore :
       une opération qui reviendrait s'y verrait aussitôt. Seul `retraits`
       porte encore quelque chose, et seulement pour le ménage d'avant. */
    var ops = { retraits: [], quantites: [], ajouts: [], proprietes: [], remplacements: [], refusAAjouter: [], etat: {} };
    var retires = Object.create(null);

    var retirer = function (ligne) {
      if (retires[ligne.key]) return;
      retires[ligne.key] = true;
      ops.retraits.push({ key: ligne.key, quantity: 0 });
    };

    /* ÉTAPE A — nettoyage des cadeaux d'avant (lot 147, décision D19).
       Le thème n'offre plus aucun produit. Une ligne encore marquée
       `_sbc_cadeau: 'auto'` vient d'un panier commencé avant le lot : elle
       part, gratuite ou facturée, quel que soit le montant du panier. Un
       carnet ACHETÉ ne porte pas la marque et reste au panier.

       Plus rien n'étant ajouté ni basculé, il n'y a plus ni gratuité à
       constater, ni refus à mémoriser, ni verdict à différer : `refusAAjouter`
       reste vide, et `listeRefus` n'est plus lue que pour l'état. */
    items.forEach(function (l) { if (estAuto(l)) retirer(l); });

    var restantes = items.filter(function (l) { return !retires[l.key]; });

    /* ÉTAPE B — les deux mesures publiées, et elles ne se mesurent pas dans la
       même unité (CLAUDE.md § 3, lot 102).
       · le sous-total : ce que la cliente PAIERA réellement pour les lignes
         retenues — remises de ligne et part allouée des remises de commande
         comprises (lot 153, P-03) — c'est lui, et lui seul, qui décide de la
         livraison offerte ;
       · le nombre de SCOOPS : il n'ouvre plus aucun droit depuis D19, il
         informe seulement l'affichage du panier.
       Exclus des deux : les lignes encore marquées cadeau, les cartes-cadeaux,
       et toute ligne sans valeur d'achat. Les lignes retirées à l'étape A ne
       comptent plus.

       LOT 153 (P-03) — avant ce lot, le sous-total sommait `original_line_price`
       (AVANT remise) : une cliente sous le seuil après un code promo voyait
       quand même « ✓ Livraison offerte », alors que Shopify facturait. La
       somme porte donc maintenant sur `final_line_price` (le prix de ligne
       APRÈS remise), plafonnée par `cart.total_price` — le vrai montant
       payé. Le plafond est le garde-fou : selon que Shopify alloue la remise
       de commande dans chaque ligne ou seulement au niveau panier, la somme
       des `final_line_price` peut rester au montant d'avant remise ; le
       plafond rend alors le calcul juste dans les deux cas.

       Mesuré au banc live le 16 septembre 2026 (complément du lot 153,
       V58 du CHANGELOG) : CORALIE166 actif, sur `POST /cart/update.js`
       Shopify alloue la remise de commande au niveau panier seulement
       (`cart_level_discount_applications`, `allocation_method:"across"`,
       `final_line_price == original_line_price` sur chaque ligne) — la
       somme des `final_line_price` reste donc au montant D'AVANT remise, et
       c'est le plafond par `cart.total_price` qui rend le calcul juste (voir
       aussi la fixture C3bis-d de `tools/verif-panier.test.mjs`, remise à
       100 %). Le supplément vidéo, porté par le prix de la ligne de scoop,
       continue de compter dans l'assiette (`docs/09_POINTS_OUVERTS.md`,
       point ⚠ Coralie inchangé — ce lot ne le tranche pas). */
    var compte = compteur();

    var sousTotal = restantes.reduce(function (total, l) {
      if (estAuto(l) || l.gift_card || nb(l.original_line_price) <= 0) return total;
      return total + nb(l.final_line_price);
    }, 0);
    var totalPanierBrut = panier && panier.total_price;
    var totalPanier = nb(totalPanierBrut);
    if (totalPanierBrut != null && sousTotal > totalPanier) sousTotal = totalPanier;
    var scoops = compte ? compte.compteScoops(restantes, IDS_SCOOPS) : 0;

    var palierLivraison = promotionLivraisonRelais(cfg) || (SEUIL_LIVRAISON > 0 && sousTotal >= SEUIL_LIVRAISON);

    /* ÉTAT — il décrit le panier tel qu'il est, pas ce qu'on veut en faire.
       Le moteur rejoue la décision jusqu'à ce qu'elle soit vide : l'état
       publié est donc toujours celui d'un panier stabilisé. Aucun champ vidéo
       n'y figure plus (lot 148) : le panier n'a rien à en dire.

       Clôture de la vague 147-150 — `gratuiteIndisponible` sort de l'état.
       Il signalait qu'une gratuité posée par le thème avait été refusée par
       Shopify ; depuis D19 le thème ne pose plus aucune ligne gratuite, la
       liste des refus reste donc vide et aucun afficheur ne lisait ce champ.
       Les refus eux-mêmes restent publiés là où ils servent encore
       (`ops.refusAAjouter`, mémoire du moteur). */
    ops.etat = {
      sousTotal: sousTotal,
      nombreScoops: scoops,
      palierLivraison: palierLivraison
    };

    return ops;
  }

  return {
    decisionCadeaux: decisionCadeaux,
    promotionLivraisonRelais: promotionLivraisonRelais,
    MARQUE: MARQUE, AUTO: AUTO
  };
});
